import classNames from 'classnames';
import React, { memo } from 'react';
import type { GenericUIProps, TransformedCSSProperties } from '../component.types';
import type { ContainerProps } from '../frame/Frame';
import frameStyles from '../frame/Frame.module.scss';
import Tooltip from '../Tooltip';
import type { BaseTextProps } from '../text';
import textStyles from '../text/Text.module.scss';
import { transformSpecialCSSProperties } from '../utils';
import styles from './Skeleton.module.scss';

interface BaseSkeletonProps
  extends Omit<GenericUIProps, 'children' | 'as'>,
    TransformedCSSProperties {
  animate?: boolean;
  borderRadius?: 'small' | 'medium' | 'large';
  tooltip?: React.ReactElement | string;
}

interface TextSkeletonProps extends BaseSkeletonProps {
  type?: 'display' | 'headline' | 'paragraph' | 'label';
  size?: BaseTextProps['size'];
  lines?: number;
  truncateLastLine?: boolean;
}

interface BoxSkeletonProps extends BaseSkeletonProps {}

interface FrameSkeletonProps
  extends Omit<BaseSkeletonProps, 'borderRadius'>,
    Pick<ContainerProps, 'size' | 'shape'> {}

const Box = memo((props: BoxSkeletonProps) => {
  const {
    animate = true,
    tooltip,
    borderRadius,
    className,
    ...finalProps
  } = transformSpecialCSSProperties(props);
  return (
    <TooltipWrapper tooltip={tooltip}>
      <div
        className={classNames(styles.skeleton, className, {
          [styles.animate]: animate,
          [styles[`border-radius-${borderRadius}`]]: !!borderRadius,
        })}
        {...finalProps}
      />
    </TooltipWrapper>
  );
});
Box.displayName = 'Skeleton.Box';

const Text = memo((props: TextSkeletonProps) => {
  const {
    type = 'paragraph',
    size = 'medium',
    animate = true,
    lines = 1,
    truncateLastLine = false,
    borderRadius = 'medium',
    tooltip,
    className,
    ...finalProps
  } = transformSpecialCSSProperties(props);

  return (
    <TooltipWrapper tooltip={tooltip}>
      <div>
        {new Array(lines).fill(0).map((_, index) => (
          <div
            key={index}
            className={classNames(
              styles.line,
              className,
              textStyles[type],
              textStyles[`size-${size}`]
            )}
            {...finalProps}
          >
            <div
              className={classNames(
                styles.skeleton,
                styles.text,
                textStyles[type],
                textStyles[`size-${size}`],
                {
                  [styles.animate]: animate,
                  [styles.truncated]: truncateLastLine && index === lines - 1,
                  [styles[`border-radius-${borderRadius}`]]: !!borderRadius,
                }
              )}
            />
          </div>
        ))}
      </div>
    </TooltipWrapper>
  );
});
Text.displayName = 'Skeleton.Text';

const Frame = memo((props: FrameSkeletonProps) => {
  const {
    animate = true,
    size,
    shape,
    tooltip,
    className,
    ...finalProps
  } = transformSpecialCSSProperties(props);
  return (
    <TooltipWrapper tooltip={tooltip}>
      <div
        className={classNames(
          styles.skeleton,
          styles.frame,
          className,
          frameStyles.container,
          frameStyles[`size-${size}`],
          frameStyles[`shape-${shape}`],
          { [styles.animate]: animate }
        )}
        {...finalProps}
      />
    </TooltipWrapper>
  );
});
Frame.displayName = 'Skeleton.Frame';

interface TooltipWrapperProps {
  tooltip?: React.ReactElement | string;
  children: React.ReactNode;
}

const TooltipWrapper = ({ children, tooltip }: TooltipWrapperProps) => {
  if (tooltip) {
    return (
      <Tooltip position="top" tooltip={tooltip}>
        {children}
      </Tooltip>
    );
  }
  return children;
};

export const Skeleton = {
  Box,
  Text,
  Frame,
};
